import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'

import AddLinkIcon from '@mui/icons-material/AddLink'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SelectAnalysisChainsModal from './SelectAnalysisChainsModal'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const CreateDevicePage = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [device, setDevice] = useState({
    name: '',
    ip: '',
    organizationId: '',
  })

  const [organizations, setOrganizations] = useState([])
  const [analyses, setAnalyses] = useState([])
  const [dataTypes, setDataTypes] = useState([])
  const [allAnalysisChains, setAllAnalysisChains] = useState([])
  const [selectedChains, setSelectedChains] = useState([])
  const [chainsModalOpen, setChainsModalOpen] = useState(false)

  const [errors, setErrors] = useState({})

  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const orgsResponse = await fetchWithAuth('organizations')
        if (orgsResponse && orgsResponse.data) {
          setOrganizations(orgsResponse.data.data || [])
        }

        const analysesResponse = await fetchWithAuth('analyses')
        if (analysesResponse && analysesResponse.data) {
          setAnalyses(analysesResponse.data.data || [])
        }

        const dataTypesResponse = await fetchWithAuth('data-types')
        if (dataTypesResponse && dataTypesResponse.data) {
          setDataTypes(dataTypesResponse.data.data || [])
        }

        const analysisChainsResponse = await fetchWithAuth('analysis-chains')
        if (analysisChainsResponse && analysisChainsResponse.data) {
          setAllAnalysisChains(analysisChainsResponse.data.data || [])
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        setError('Failed to load required data. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setDevice((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!device.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (device.name.length > 100) {
      newErrors.name = 'Name must be less than 100 characters'
    }

    if (!device.organizationId) {
      newErrors.organizationId = 'Organization is required'
    }

    if (device.ip && !isValidIP(device.ip)) {
      newErrors.ip = 'Please enter a valid IP address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidIP = (ip) => {
    const ipRegex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
    return !ip || ipRegex.test(ip)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      setErrors({})

      const payload = {
        name: device.name.trim(),
        common_name: device.common_name.trim(),
        status: device.status,
        organizationId: device.organizationId,
        analysisChains: selectedChains.map((chain) => chain.uuid),
      }

      if (device.ip) payload.ip = device.ip

      const response = await fetchWithAuth('devices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (response.status >= 400) {
        throw response
      }

      navigate('/devices')
    } catch (error) {
      console.error('Error creating device:', error)

      if (error.data && error.data.errors) {
        const apiErrors = {}

        error.data.errors.forEach((err) => {
          apiErrors[err.field] = err.message
        })

        setErrors(apiErrors)
        setError('Please correct the errors in the form')
      } else if (error.data && error.data.message) {
        setError(error.data.message)
      } else {
        setError('Failed to create device. Please try again.')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleChainsSave = (newChainIds) => {
    const selectedChainIds = Array.from(newChainIds.ids)
    const selected = selectedChainIds
      .map((id) => allAnalysisChains.find((chain) => chain.uuid === id))
      .filter(Boolean)
    setSelectedChains(selected)
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton component={Link} to="/devices" edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Create New Device
        </Typography>
      </Stack>

      <Activity mode={error ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Activity>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container size={12} spacing={3}>
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  Basic Information
                </Typography>

                <TextField
                  label="Name"
                  name="name"
                  value={device.name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />

                <TextField
                  label="Common Name"
                  name="common_name"
                  value={device.common_name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.common_name}
                  helperText={errors.common_name}
                  required
                />

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.organizationId}
                  required
                >
                  <InputLabel id="organization-label" htmlFor="organization">
                    Organization
                  </InputLabel>
                  <Select
                    labelId="organization-label"
                    name="organizationId"
                    value={device.organizationId}
                    onChange={handleInputChange}
                    label="Organization"
                    inputProps={{ id: 'organization' }}
                  >
                    {organizations && organizations.length > 0 ? (
                      organizations.map((org) => (
                        <MenuItem key={org.uuid} value={org.uuid}>
                          {org.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No organizations available</MenuItem>
                    )}
                  </Select>
                  <Activity mode={errors.organizationId ? 'visible' : 'hidden'}>
                    <FormHelperText error>
                      {errors.organizationId}
                    </FormHelperText>
                  </Activity>
                </FormControl>
                <TextField
                  label="IP Address"
                  name="ip"
                  value={device.ip || ''}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.ip}
                  helperText={errors.ip}
                />
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" component="h2">
                    Analysis Chains
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddLinkIcon />}
                    onClick={() => setChainsModalOpen(true)}
                    size="small"
                  >
                    Select Chains
                  </Button>
                </Stack>

                {selectedChains && selectedChains.length > 0 ? (
                  <Box spacing={1}>
                    {selectedChains.map((chain) => (
                      <Chip
                        key={chain._id}
                        label={chain.name ?? chain._id}
                        onDelete={() => {
                          setSelectedChains(
                            selectedChains.filter((c) => c._id !== chain._id),
                          )
                        }}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No analysis chains selected.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {saving ? 'Creating...' : 'Create Device'}
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/devices"
            disabled={saving}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <SelectAnalysisChainsModal
        open={chainsModalOpen}
        onClose={() => setChainsModalOpen(false)}
        onSave={handleChainsSave}
        selectedChains={selectedChains}
      />
    </Box>
  )
}

export default CreateDevicePage
